import { useCallback, useState } from 'react';

export type ClientSortOrder = 'asc' | 'desc';

export interface ServerSortState<TField extends string> {
  sortBy: TField;
  sortOrder: ClientSortOrder;
}

/** Next sort after a header click: same column flips direction, a new column starts ascending. */
export function nextServerSort<TField extends string>(
  prev: ServerSortState<TField>,
  field: TField,
): ServerSortState<TField> {
  if (prev.sortBy === field) {
    return { sortBy: field, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' };
  }
  return { sortBy: field, sortOrder: 'asc' };
}

interface UseServerSortOptions<TField extends string> {
  /** Backend whitelist for `sortBy`; header clicks on any other field are ignored. */
  fields: readonly TField[];
  /** Must be a stable reference (module constant). */
  defaultSort: ServerSortState<TField>;
  /** Runs after every header click, e.g. to go back to page 1. Must be stable. */
  onSortChange?: () => void;
}

/**
 * Sort state for tables sorted by the backend over the whole list (not just the visible page).
 * `apiSortOrder` is the `ASC`/`DESC` value the API expects.
 */
export function useServerSort<TField extends string>({
  fields,
  defaultSort,
  onSortChange,
}: UseServerSortOptions<TField>) {
  const [sort, setSort] = useState<ServerSortState<TField>>(defaultSort);

  const requestSort = useCallback(
    (field: string) => {
      const sortField = fields.find((allowed) => allowed === field);
      if (sortField == null) {
        return;
      }
      setSort((prev) => nextServerSort(prev, sortField));
      onSortChange?.();
    },
    [fields, onSortChange],
  );

  const resetSort = useCallback(() => setSort(defaultSort), [defaultSort]);

  return {
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
    apiSortOrder: sort.sortOrder === 'asc' ? ('ASC' as const) : ('DESC' as const),
    requestSort,
    resetSort,
  };
}
